import { useState, useMemo } from 'react';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Modal } from '@/components/molecules';
import {
  useSubcategoriesList,
  useCategoriesList,
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
} from '@/hooks/useErpApi';

interface Subcategory {
  id: string;
  name: string;
  description?: string;
  category_id: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface SubcategoryFormData {
  name: string;
  description: string;
  category_id: string;
}

export default function SubcategoriesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const [formData, setFormData] = useState<SubcategoryFormData>({
    name: '',
    description: '',
    category_id: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: subcategoriesData, isLoading, error: queryError, refetch } = useSubcategoriesList();
  const { data: categoriesData } = useCategoriesList();
  const { mutate: createSubcategory, isLoading: createLoading } = useCreateSubcategoryMutation();
  const { mutate: updateSubcategory, isLoading: updateLoading } = useUpdateSubcategoryMutation();
  const { mutate: deleteSubcategory, isLoading: deleteLoading } = useDeleteSubcategoryMutation();

  // Extract data from API responses
  // The API returns { data: [...], pagination: {...}, ... }
  // The useQuery hook extracts response.data, so the data should be the array
  // But handle both cases: if it's an array use it, if it has a data property use that
  let subcategories: Subcategory[] = [];
  if (subcategoriesData) {
    if (Array.isArray(subcategoriesData)) {
      subcategories = subcategoriesData;
    } else if ((subcategoriesData as any)?.data && Array.isArray((subcategoriesData as any).data)) {
      subcategories = (subcategoriesData as any).data;
    }
  }
  
  let categories: Category[] = [];
  if (categoriesData) {
    if (Array.isArray(categoriesData)) {
      categories = categoriesData;
    } else if ((categoriesData as any)?.data && Array.isArray((categoriesData as any).data)) {
      categories = (categoriesData as any).data;
    }
  }

  // Create a map of category ID to category name for display
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((cat) => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  // Filter subcategories
  const filteredSubcategories = subcategories.filter((subcat) => {
    const matchesSearch =
      subcat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subcat.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      categoryMap[subcat.category_id]?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategoryFilter === 'all' || subcat.category_id === selectedCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleAddSubcategory = () => {
    setFormData({ name: '', description: '', category_id: '' });
    setSelectedSubcategory(null);
    setError(null);
    setIsAddModalOpen(true);
  };

  const handleEditSubcategory = (subcategory: Subcategory) => {
    setFormData({
      name: subcategory.name,
      description: subcategory.description || '',
      category_id: subcategory.category_id,
    });
    setSelectedSubcategory(subcategory);
    setError(null);
    setIsEditModalOpen(true);
  };

  const handleDeleteSubcategory = async (subcategoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this subcategory? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteSubcategory(subcategoryId);
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete subcategory');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Subcategory name is required');
      return;
    }

    if (!formData.category_id) {
      setError('Category is required');
      return;
    }

    try {
      if (selectedSubcategory) {
        await updateSubcategory({
          id: selectedSubcategory.id,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
            category_id: formData.category_id,
          },
        });
        setIsEditModalOpen(false);
      } else {
        await createSubcategory({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          category_id: formData.category_id,
        });
        setIsAddModalOpen(false);
      }
      setFormData({ name: '', description: '', category_id: '' });
      setSelectedSubcategory(null);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save subcategory');
    }
  };

  const handleFormCancel = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setFormData({ name: '', description: '', category_id: '' });
    setSelectedSubcategory(null);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-600">Loading subcategories...</div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-error-600">Error loading subcategories: {queryError.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Subcategories</h1>
          <p className="text-neutral-600 mt-1">Manage product subcategories within categories</p>
        </div>
        <Button variant="primary" onClick={handleAddSubcategory}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Subcategory
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Search</label>
            <Input
              type="text"
              placeholder="Search subcategories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Filter by Category</label>
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Subcategories List */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-6">
          {filteredSubcategories.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Icon name="FolderTree" size={48} className="mx-auto mb-4 text-neutral-400" />
              <p className="text-lg font-medium mb-2">No subcategories found</p>
              <p className="text-sm">
                {searchQuery || selectedCategoryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first subcategory'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {filteredSubcategories.map((subcat) => (
                    <tr key={subcat.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-neutral-900">{subcat.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="info">{categoryMap[subcat.category_id] || 'Unknown'}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-neutral-600">
                          {subcat.description || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEditSubcategory(subcat)}
                            disabled={deleteLoading}
                          >
                            <Icon name="Pencil" size={16} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="error"
                            size="sm"
                            onClick={() => handleDeleteSubcategory(subcat.id)}
                            disabled={deleteLoading}
                          >
                            <Icon name="Trash2" size={16} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Subcategory Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleFormCancel}
        title="Add Subcategory"
        size="md"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3">
              <p className="text-error-700 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Category *
            </label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Subcategory Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., WATER_SOLUBLE"
              required
              className="w-full"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Name will be automatically normalized to UPPER_SNAKE_CASE
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Subcategory description..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
            <Button type="button" variant="secondary" onClick={handleFormCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create Subcategory'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Subcategory Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleFormCancel}
        title="Edit Subcategory"
        size="md"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3">
              <p className="text-error-700 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Category *
            </label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Subcategory Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., WATER_SOLUBLE"
              required
              className="w-full"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Name will be automatically normalized to UPPER_SNAKE_CASE
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Subcategory description..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
            <Button type="button" variant="secondary" onClick={handleFormCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={updateLoading}>
              {updateLoading ? 'Updating...' : 'Update Subcategory'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

